import { h, ref, VNodeChild } from "vue";
import { useDialog, NButton, DialogReactive } from "naive-ui";

interface UseActionDialogOptions {
  title?: string;
  content?: string | (() => VNodeChild);
  showIcon?: boolean;
  icon?: () => VNodeChild; // 아이콘을 위한 prop 추가
  maskClosable?: boolean;
  closable?: boolean;
  positiveText?: string;
  negativeText?: string;
  onCancel?: () => void | Promise<void>;
  onConfirm: () => Promise<void>;
  positiveButtonProps?: Partial<InstanceType<typeof NButton>["$props"]>;
  negativeButtonProps?: Partial<InstanceType<typeof NButton>["$props"]>;
  confirmButtonLoadingText?: string;
}

export function useActionDialog() {
  const dialogApi = useDialog();

  const show = (options: UseActionDialogOptions) => {
    const {
      title = "작업 확인",
      content = "정말로 이 작업을 수행하시겠습니까?",
      showIcon = false,
      icon,
      maskClosable = false,
      closable = false,
      positiveText = "확인",
      negativeText = "취소",
      onCancel,
      onConfirm,
      positiveButtonProps = { type: "primary" },
      negativeButtonProps = {},
      confirmButtonLoadingText = "처리 중...",
    } = options;

    const isLoading = ref(false); // 현재 다이얼로그의 확인 버튼 로딩 상태
    let dialogInstance: DialogReactive | null = null;

    const destroyCurrentDialog = () => {
      if (dialogInstance) {
        dialogInstance.destroy();
        dialogInstance = null;
      }
    };

    const handleCancel = async () => {
      if (onCancel) {
        try {
          await onCancel();
        } catch (e) {
          console.error("onCancel 콜백 실행 중 오류 발생:", e);
        }
      }
      destroyCurrentDialog();
    };

    const handleConfirm = async () => {
      if (isLoading.value) return;
      isLoading.value = true;
      try {
        await onConfirm();
      } catch (error) {
        console.error("onConfirm 콜백 실행 중 오류 발생:", error);
      } finally {
        isLoading.value = false;
        destroyCurrentDialog();
      }
    };

    const renderContent =
      typeof content === "function" ? content : () => content;

    dialogInstance = dialogApi.create({
      title,
      content: renderContent,
      showIcon,
      icon,
      maskClosable,
      closable,
      positiveText: undefined,
      negativeText: undefined,
      action: () =>
        h(
          "div",
          { style: "display: flex; justify-content: flex-end; gap: 8px;" },
          [
            h(
              NButton,
              { ...negativeButtonProps, onClick: handleCancel },
              () => negativeText
            ),
            h(
              NButton,
              {
                ...positiveButtonProps,
                loading: isLoading.value,
                onClick: handleConfirm,
              },
              () => (isLoading.value ? confirmButtonLoadingText : positiveText)
            ),
          ]
        ),
    });
  };

  return {
    show,
  };
}
